import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Loader2, Edit, Trash2, Package, Armchair, Sun, Umbrella, Tent } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ICONS = [
  { value: 'armchair', label: 'Chair', Icon: Armchair },
  { value: 'sun', label: 'Sun', Icon: Sun },
  { value: 'umbrella', label: 'Umbrella', Icon: Umbrella },
  { value: 'tent', label: 'Tent/Cabana', Icon: Tent },
  { value: 'package', label: 'Generic', Icon: Package },
];

export default function InventoryTypesManager() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [customTypes, setCustomTypes] = useState([]);
  const [defaultTypes, setDefaultTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: 'armchair',
    default_capacity: 2,
    default_price: 0
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchTypes();
    }
  }, [selectedProperty]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API_URL}/properties`);
      setProperties(response.data);
      if (response.data.length > 0) {
        setSelectedProperty(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load properties');
    }
  };

  const fetchTypes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/inventory-types?property_id=${selectedProperty}`);
      setCustomTypes(response.data.custom_types || []);
      setDefaultTypes(response.data.default_types || []);
    } catch (error) {
      toast.error('Failed to load inventory types');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  };

  const handleNameChange = (name) => {
    setFormData({
      ...formData,
      name: name,
      slug: editingType ? formData.slug : generateSlug(name)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const data = {
        property_id: selectedProperty,
        name: formData.name,
        slug: formData.slug,
        description: formData.description || null,
        icon: formData.icon,
        default_capacity: parseInt(formData.default_capacity) || 2,
        default_price: parseFloat(formData.default_price) || 0
      };

      if (editingType) {
        await axios.put(`${API_URL}/inventory-types/${editingType.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Inventory type updated');
      } else {
        await axios.post(`${API_URL}/inventory-types`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Inventory type created');
      }

      setDialogOpen(false);
      setEditingType(null);
      resetForm();
      fetchTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save inventory type');
    }
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      slug: type.slug,
      description: type.description || '',
      icon: type.icon || 'armchair',
      default_capacity: type.default_capacity || 2,
      default_price: type.default_price || 0
    });
    setDialogOpen(true);
  };

  const handleDelete = async (typeId) => {
    if (!window.confirm('Are you sure you want to delete this inventory type?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/inventory-types/${typeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Inventory type deleted');
      fetchTypes();
    } catch (error) {
      toast.error('Failed to delete inventory type');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      icon: 'armchair',
      default_capacity: 2,
      default_price: 0
    });
  };

  const openNewDialog = () => {
    setEditingType(null);
    resetForm();
    setDialogOpen(true);
  };

  const getIcon = (iconName) => {
    const iconConfig = ICONS.find(i => i.value === iconName);
    if (iconConfig) {
      const IconComponent = iconConfig.Icon;
      return <IconComponent className="w-5 h-5" />;
    }
    return <Package className="w-5 h-5" />;
  };

  return (
    <AdminLayout title="Inventory Types">
      {/* Property Selector */}
      <div className="mb-6 flex gap-4 items-end">
        <div className="flex-1 max-w-md">
          <Label htmlFor="property">Property</Label>
          <select
            id="property"
            value={selectedProperty}
            onChange={(e) => setSelectedProperty(e.target.value)}
            className="w-full mt-2 p-3 border border-[#E7E5E4] rounded-sm"
            data-testid="select-property"
          >
            {properties.map(prop => (
              <option key={prop.id} value={prop.id}>{prop.name}</option>
            ))}
          </select>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={openNewDialog}
              className="bg-[#9F1239] hover:bg-[#881337] text-white"
              data-testid="btn-add-type"
            >
              <Plus className="mr-2 h-4 w-4" /> Add Custom Type
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-serif">
                {editingType ? 'Edit Inventory Type' : 'Create Inventory Type'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Display Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g., Premium Cabana"
                  required
                  data-testid="input-name"
                />
              </div>

              <div>
                <Label htmlFor="slug">System Identifier *</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') })}
                  placeholder="e.g., premium_cabana"
                  required
                  data-testid="input-slug"
                />
                <p className="text-xs text-[#78716C] mt-1">Used internally. Auto-generated from name.</p>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="A luxurious poolside cabana with premium amenities..."
                  rows={3}
                  data-testid="textarea-description"
                />
              </div>

              <div>
                <Label>Icon</Label>
                <div className="flex gap-2 mt-2">
                  {ICONS.map(({ value, label, Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon: value })}
                      className={`p-3 rounded-sm border-2 transition-all ${
                        formData.icon === value
                          ? 'border-[#9F1239] bg-[#FEF2F2]'
                          : 'border-[#E7E5E4] hover:border-[#9F1239]'
                      }`}
                      title={label}
                      data-testid={`icon-${value}`}
                    >
                      <Icon className={`w-5 h-5 ${formData.icon === value ? 'text-[#9F1239]' : 'text-[#78716C]'}`} />
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="default_capacity">Default Capacity</Label>
                  <Input
                    id="default_capacity"
                    type="number"
                    min="1"
                    value={formData.default_capacity}
                    onChange={(e) => setFormData({ ...formData, default_capacity: e.target.value })}
                    data-testid="input-capacity"
                  />
                </div>
                <div>
                  <Label htmlFor="default_price">Default Price ($)</Label>
                  <Input
                    id="default_price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.default_price}
                    onChange={(e) => setFormData({ ...formData, default_price: e.target.value })}
                    data-testid="input-price"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#9F1239] hover:bg-[#881337]"
                data-testid="btn-submit"
              >
                {editingType ? 'Update Type' : 'Create Type'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#9F1239]" />
        </div>
      ) : (
        <Tabs defaultValue="custom" className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md mb-6">
            <TabsTrigger value="custom">Custom Types ({customTypes.length})</TabsTrigger>
            <TabsTrigger value="default">Default Types ({defaultTypes.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="custom">
            {customTypes.length === 0 ? (
              <Card className="bg-white border-slate-200 shadow-sm rounded-md">
                <CardContent className="py-12 text-center">
                  <Package className="w-12 h-12 text-[#A8A29E] mx-auto mb-4" />
                  <p className="text-[#78716C]">No custom inventory types created yet.</p>
                  <p className="text-sm text-[#A8A29E] mt-2">
                    Create custom types to better categorize your inventory (e.g., "Family Cabana", "VIP Daybed").
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {customTypes.map((type) => (
                  <Card key={type.id} className="bg-white border-slate-200 shadow-sm rounded-md" data-testid={`type-${type.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#FEF2F2] flex items-center justify-center text-[#9F1239]">
                            {getIcon(type.icon)}
                          </div>
                          <div>
                            <h3 className="font-serif font-medium text-[#1C1917]">{type.name}</h3>
                            <p className="text-xs text-[#A8A29E]">{type.slug}</p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(type)}
                            data-testid={`btn-edit-${type.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(type.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`btn-delete-${type.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {type.description && (
                        <p className="text-sm text-[#78716C] mb-4 line-clamp-2">{type.description}</p>
                      )}
                      
                      <div className="flex gap-4 text-sm">
                        <span className="text-[#78716C]">Capacity: <span className="font-medium text-[#1C1917]">{type.default_capacity}</span></span>
                        <span className="text-[#78716C]">Price: <span className="font-medium text-[#9F1239]">${type.default_price}</span></span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="default">
            <Card className="bg-[#FAFAF9] border-[#E7E5E4] mb-4">
              <CardContent className="py-4">
                <p className="text-sm text-[#78716C]">
                  These are built-in inventory types. They cannot be edited or deleted, but you can create custom types with similar names if needed.
                </p>
              </CardContent>
            </Card>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {defaultTypes.map((type, index) => (
                <Card key={index} className="bg-white border-slate-200 shadow-sm rounded-md opacity-75">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-[#F5F5F4] flex items-center justify-center text-[#78716C]">
                        <Package className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-serif font-medium text-[#1C1917]">{type.name}</h3>
                        <p className="text-xs text-[#A8A29E]">{type.slug}</p>
                      </div>
                    </div>
                    <div className="flex gap-4 text-sm">
                      <span className="text-[#78716C]">Capacity: <span className="font-medium text-[#1C1917]">{type.default_capacity}</span></span>
                      <span className="text-[#78716C]">Price: <span className="font-medium text-[#78716C]">${type.default_price}</span></span>
                    </div>
                    <p className="text-xs text-[#A8A29E] mt-3 italic">Built-in type</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      )}
    </AdminLayout>
  );
}
